import { defineStore } from 'pinia';
import { ref } from 'vue';
import { i18n } from '../i18n/index';
import type { CommerceCart, CommerceCartResponse } from '../types/commerce';

function getCsrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

function getSiteId(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="craft-site-id"]')?.content ?? '';
}

async function postToCart(params: Record<string, string>): Promise<CommerceCartResponse> {
    const response = await fetch('/actions/commerce/cart/update-cart', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
            'X-CSRF-Token': getCsrfToken(),
            'X-Craft-Site': getSiteId(),
        },
        body: new URLSearchParams({ clearNotices: '1', ...params }),
    });
    if (response.status >= 500) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as CommerceCartResponse;
}

export const useCartStore = defineStore('cart', () => {
    const cart = ref<CommerceCart | null>(null);
    const errors = ref<string[] | null>(null);

    function applyResponse(data: CommerceCartResponse): void {
        cart.value = data.originalCart ?? data.cart;
        errors.value = data.errors ? Object.values(data.errors).flat() : null;
    }

    const awaitCart = new Promise<CommerceCart>((resolve, reject) => {
        fetch('/actions/commerce/cart/get-cart', { headers: { Accept: 'application/json', 'X-Craft-Site': getSiteId() } })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json() as Promise<CommerceCartResponse>;
            })
            .then((data) => {
                cart.value = data.cart;
                resolve(data.cart);
            })
            .catch((e) => {
                errors.value = [e instanceof Error ? e.message : i18n.global.t('cart.errors.loadFailed')];
                reject(e);
            });
    });

    async function addToCart(purchasableId: number, qty = 1): Promise<void> {
        errors.value = null;
        try {
            applyResponse(await postToCart({
                'purchasables[0][id]': String(purchasableId),
                'purchasables[0][qty]': String(qty),
            }));
        } catch (e) {
            errors.value = [e instanceof Error ? e.message : i18n.global.t('cart.errors.addFailed')];
        }
    }

    async function updateLineItemQty(lineItemId: number, qty: number): Promise<void> {
        errors.value = null;
        try {
            applyResponse(await postToCart({
                [`lineItems[${lineItemId}][qty]`]: String(qty),
            }));
        } catch (e) {
            errors.value = [e instanceof Error ? e.message : i18n.global.t('cart.errors.updateFailed')];
        }
    }

    async function removeLineItem(lineItemId: number): Promise<void> {
        errors.value = null;
        try {
            applyResponse(await postToCart({
                [`lineItems[${lineItemId}][remove]`]: '1',
            }));
        } catch (e) {
            errors.value = [e instanceof Error ? e.message : i18n.global.t('cart.errors.removeFailed')];
        }
    }

    async function setCouponCode(code: string): Promise<void> {
        errors.value = null;
        try {
            applyResponse(await postToCart({ couponCode: code }));
        } catch (e) {
            errors.value = [e instanceof Error ? e.message : i18n.global.t('cart.errors.couponFailed')];
        }
    }

    async function updateCart(params: Record<string, string>): Promise<void> {
        errors.value = null;
        try {
            applyResponse(await postToCart(params));
        } catch (e) {
            errors.value = [e instanceof Error ? e.message : i18n.global.t('cart.errors.updateFailed')];
        }
    }

    return {
        cart,
        awaitCart,
        errors,
        addToCart,
        updateLineItemQty,
        removeLineItem,
        setCouponCode,
        updateCart,
    };
});


